import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  Event as EventIcon,
  Description as DescriptionIcon,
  Receipt as ReceiptIcon,
  Inventory as InventoryIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  AccountBalance as TaxIcon,
  LocalOffer as PackageIcon,
  Assignment as EInvoiceIcon,
  Assessment as ReportsIcon,
  Book as BookIcon,
  AccountBalance as AccountBalanceIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext.js';
import ClinicSettingsService from '../services/ClinicSettingsService.js';

const drawerWidth = 240;

const MainLayout = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [clinicSettings, setClinicSettings] = useState(() => ClinicSettingsService.getCached());

  const companyName = clinicSettings?.companyName ?? clinicSettings?.CompanyName ?? 'Clinic Management';
  const logoDataUrl = clinicSettings?.logoDataUrl ?? clinicSettings?.LogoDataUrl ?? null;

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      try {
        const res = await ClinicSettingsService.getSettings();
        const data = res?.data ?? res;
        if (isMounted) {
          setClinicSettings(data);
        }
      } catch {
      }
    };

    const onUpdated = () => {
      load();
    };

    load();
    window.addEventListener('clinicSettingsUpdated', onUpdated);

    return () => {
      isMounted = false;
      window.removeEventListener('clinicSettingsUpdated', onUpdated);
    };
  }, []);

  useEffect(() => {
    document.title = companyName || 'Clinic Management';
  }, [companyName]);
  
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  
  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };
  
  const handleLogout = async () => {
    handleProfileMenuClose();
    await logout();
    navigate('/login');
  };
  
  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };
  
  // Check if user has admin role to show admin-only menu items
  const isAdmin = Array.isArray(currentUser?.roles) && currentUser.roles.includes('Administrator');
  
  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { text: 'Reports', icon: <ReportsIcon />, path: '/reports' },
    { text: 'Patients', icon: <PersonIcon />, path: '/patients' },
    { text: 'Appointments', icon: <EventIcon />, path: '/appointments' },
    { text: 'Medical Records', icon: <DescriptionIcon />, path: '/medical-records' },
    { text: 'Billing', icon: <ReceiptIcon />, path: '/billing' },
    { text: 'E-Invoices', icon: <EInvoiceIcon />, path: '/einvoices' },
    { text: 'Inventory', icon: <InventoryIcon />, path: '/inventory' },
    { text: 'Packages', icon: <PackageIcon />, path: '/packages' },
    // Only show Admin menu items for administrators
    ...(isAdmin ? [
      { text: 'User Management', icon: <SettingsIcon />, path: '/admin/users' },
      { text: 'Holiday Management', icon: <EventIcon />, path: '/admin/holidays' },
      { text: 'Tax Configuration', icon: <TaxIcon />, path: '/admin/tax-configuration' },
      { text: 'AI Configuration', icon: <SettingsIcon />, path: '/admin/ai-configuration' },
      { text: 'Clinic Settings', icon: <SettingsIcon />, path: '/admin/clinic-settings' },
      { text: 'Chart of Accounts', icon: <AccountBalanceIcon />, path: '/accounting/chart-of-accounts' },
      { text: 'Journal Entries', icon: <BookIcon />, path: '/accounting/journal-entries' }
    ] : []),
  ];
  
  const drawer = (
    <div>
      <Toolbar sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 1 }}>
        <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
          {logoDataUrl ? (
            <Avatar src={logoDataUrl} variant="rounded" sx={{ width: 32, height: 32 }} />
          ) : (
            <Avatar variant="rounded" sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
              {String(companyName || 'C').slice(0, 1).toUpperCase()}
            </Avatar>
          )}
          <Typography
            variant="h6"
            component="div"
            color="primary"
            fontWeight="bold"
            sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {companyName}
          </Typography>
        </Box>
        {isMobile && (
          <IconButton onClick={handleDrawerToggle} sx={{ ml: 1 }}>
            <ChevronLeftIcon />
          </IconButton>
        )}
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => handleNavigation(item.path)}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  borderRight: `3px solid ${theme.palette.primary.main}`,
                },
                '&.Mui-selected:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.12)',
                },
              }}
            >
              <ListItemIcon>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  );
  
  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          boxShadow: 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {menuItems.find(item => item.path === location.pathname)?.text || 'Dashboard'}
          </Typography>
          
          <Tooltip title="Account settings">
            <IconButton
              onClick={handleProfileMenuOpen}
              size="small"
              sx={{ ml: 2 }}
              aria-controls={Boolean(anchorEl) ? 'account-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={Boolean(anchorEl) ? 'true' : undefined}
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main }}>
                {currentUser?.username?.charAt(0).toUpperCase() || 'U'}
              </Avatar>
            </IconButton>
          </Tooltip>
          
          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            onClick={handleProfileMenuClose}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                mt: 1.5,
                '& .MuiAvatar-root': {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem disabled>
              <ListItemText
                primary={currentUser?.username || 'Unknown user'}
                secondary={Array.isArray(currentUser?.roles) && currentUser.roles.length > 0 ? currentUser.roles.join(', ') : null}
              />
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/profile'); }}>
              <Avatar /> Profile
            </MenuItem>
            <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/settings'); }}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              Settings
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Toolbar /> {/* This empty Toolbar creates space at the top equal to the AppBar height */}
        <Outlet />
      </Box>
    </Box>
  );
};

export default MainLayout;
